/**
 * kisiFirmaEsleService.js — "Bu firma fuarda çalışabilir mi?" ve firmayı fuara eşleme
 *
 * Kullanım: Components/Kisi/PersonelEkleModal + FirmaFuarEsleModal (Kişi Listesi > Personel Ekle)
 *
 * İş kuralı (ürün sahibi): bir kişi fuarda ancak fuara EŞLENMİŞ bir firma adına çalışabilir —
 *   (a) firma o fuarda bir standda çalışıyor, ya da
 *   (b) firma o fuarda bir dekoratörün taşeronu.
 * ⚠️ Backend bunu ZORLAMIYOR: `POST /personnel/yeni` fuar bağına bakmıyor. Kuralı bu servis tutuyor.
 *
 * Uçlar:
 *   (a) okuma  GET  /firma/getFirmaStands?firmaId=&fuarId=   → [{stand_id, stand_name, salon_name}]
 *              (salon_stand_firma_to_fuar'dan okur; firma başına satır tekrarlanabilir)
 *       yazma  POST /standTalep/assignFirma/{standId}          (standOverviewService.assignFirmaToStand)
 *              firma_to_fuar YAZMAZ → yeni firmada ayrıca POST /firma/esle (salon+stand ile;
 *              boş gönderilirse stand listelerinde hayalet yerleşim satırı oluşur). /esle
 *              idempotent değil, bu yüzden yalnızca bu akışta yeni açılan firmada çağrılır.
 *   (b) okuma  GET  /firma-taseron/taseron/{firmaId}?fuarId=   (firmaTaseronService)
 *       yazma  POST /firma-taseron — taşeronun firma_to_fuar satırını kendisi yazar. Bağlantı
 *              `durum 0` doğar (taşeron/dekoratör onayları sonra); SAKA görevlendirmesini engellemez.
 *              Yalnızca personel çağırabilir. `taseronFirmaTypeId` (hizmet tipi, >= 4) JSON SAYISI olmalı —
 *              metin gönderilirse backend sessizce yok sayar (NULL kalır, tip 4 varsayılır).
 *   dekoratör araması  GET /firma/list?fuarId=&firmaTypeId=3&search= (yalnızca fuara eşli firmalar)
 *   stand araması      GET /standTalep/search?fuarId=&q=&salonId=&limit= (backlog #133) — salon İSTEĞE BAĞLI;
 *                      `q` stand adı / kodu / id'si VE standa bağlı her firmanın adı (katılımcı, dekoratör,
 *                      taşeron); katılımcısı olmayan standlar da gelir; hafif, sayfa 50 ile sınırlı.
 *                      (Eski standBySalonId salon istiyor, yalnız stand adına bakıyor ve katılımcısız standı
 *                      gizliyordu; `/standTalep/list` pill alanları yüzünden fuar 298'de ~20 sn sürüyor.)
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { assignFirmaToStand, getSalonOptions } from './standOverviewService';
import { esleFirmaToFuar, FIRMA_TYPE } from './firmaAramaService';
import { getBeniTaseronGosterenler, createTaseronBaglantisi } from './firmaTaseronService';
import i18n from "../i18n";

const FIRMA_API = `${JAVA_BACKEND_API_URL}/firma`;

export { FIRMA_TYPE };

export const STAND_ROLLERI = [
  { id: FIRMA_TYPE.KATILIMCI, get label() { return i18n.t("services:kisiFirmaEsleService.katilimci"); } },
  { id: FIRMA_TYPE.DEKORATOR, get label() { return i18n.t("services:kisiFirmaEsleService.dekorator"); } },
  { id: FIRMA_TYPE.TASERON, get label() { return i18n.t("services:kisiFirmaEsleService.taseron"); } },
];

function parseError(error) {
  const message =
    typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:kisiFirmaEsleService.bilinmeyenHata");
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  throw wrapped;
}

/**
 * Firmanın bu fuardaki bağları.
 * @returns {{ standlar: {standId, standName, salonName}[], dekoratorler: {id, anaFirmaId, anaFirmaAdi}[], esli: boolean }}
 */
export async function getFirmaFuarBaglari(firmaId, fuarId) {
  try {
    const [standRes, taseron] = await Promise.all([
      axios.get(`${FIRMA_API}/getFirmaStands`, { params: { firmaId, fuarId }, headers: getAuthHeader() }),
      getBeniTaseronGosterenler(firmaId, { fuarId, sadeceAktif: true }),
    ]);
    const seen = new Set();
    const standlar = (standRes.data || [])
      .filter((r) => !seen.has(r.stand_id) && seen.add(r.stand_id))
      .map((r) => ({ standId: r.stand_id, standName: r.stand_name, salonName: r.salon_name }));
    const dekoratorler = (taseron || []).map((t) => ({ id: t.id, anaFirmaId: t.anaFirmaId, anaFirmaAdi: t.anaFirmaAdi }));
    return { standlar, dekoratorler, esli: standlar.length > 0 || dekoratorler.length > 0 };
  } catch (error) {
    parseError(error);
  }
}

/** Fuara eşli dekoratör firmalar (arama). */
export async function searchFuarDekoratorleri(fuarId, search = '', size = 8) {
  try {
    const res = await axios.get(`${FIRMA_API}/list`, {
      params: { fuarId, firmaTypeId: FIRMA_TYPE.DEKORATOR, search: search || undefined, page: 0, size },
      headers: getAuthHeader(),
    });
    return (res.data?.content || []).map((f) => ({ firmaId: f.firmaId, firmaName: f.firmaName }));
  } catch (error) {
    parseError(error);
  }
}

/** Fuarın salonları (GET /fuar/{fuarId} → fuarSalons). */
export function getFuarSalonlari(fuarId) {
  return getSalonOptions(fuarId, { forceLive: true });
}

/**
 * Fuar geneli stand araması — salon seçmeden; stand adı/kodu/id'si ya da standdaki herhangi bir firmanın
 * adıyla. Boş `q` fuarın ilk sayfasını (alfabetik) döndürür; bunu istemeyen çağıran hiç sormaz.
 * Dönen `salonId` backend'in seçtiği salondur (çağıranın süzgeci > standın kendi fuarı > ad öneki) ve
 * POST /firma/esle'ye aynen gider.
 * @returns {{ items: {standId, standName, standCode, salonId, salonName,
 *             firmalar: {firmaId, firmaName, firmaTypeId, firmaTypeName, relationLevel}[]}[], hasMore: boolean }}
 */
export async function searchStandlar(fuarId, { q = '', salonId = null, limit = 20 } = {}) {
  try {
    const res = await axios.get(`${JAVA_BACKEND_API_URL}/standTalep/search`, {
      params: { fuarId, q: q || undefined, salonId: salonId || undefined, limit },
      headers: getAuthHeader(),
    });
    const d = res.data || {};
    return {
      items: (d.items || []).map((r) => ({ ...r, firmalar: r.firmalar || [] })),
      hasMore: Boolean(d.hasMore),
    };
  } catch (error) {
    parseError(error);
  }
}

/** (a) Firmayı bir standa bağla. `yeniFirma` = bu akışta yeni açıldı → firma_to_fuar da yazılır. */
export async function firmayiStandaEsle({ firmaId, fuarId, stand, firmaTypeId, yeniFirma = false }) {
  await assignFirmaToStand(stand.standId, { firmaId, firmaTypeId, fuarId, relationLevel: 1 }, { forceLive: true });
  if (yeniFirma) {
    await esleFirmaToFuar(
      { firmaId, fuarId, firmaTypeId, salonId: stand.salonId ?? null, standId: stand.standId },
      { forceLive: true },
    );
  }
}

/** (b) Firmayı bir dekoratörün taşeronu olarak fuara bağla. `taseronFirmaTypeId` (isteğe bağlı) hizmet tipidir. */
export async function firmayiTaseronOlarakEsle({ firmaId, fuarId, dekoratorFirmaId, taseronFirmaTypeId }) {
  return createTaseronBaglantisi({
    fuarId, anaFirmaId: dekoratorFirmaId, taseronFirmaId: firmaId, taseronFirmaTypeId,
  });
}
